import React from "react";
import { Box, Badge, useColorModeValue } from "@chakra-ui/react";
import { Lock } from "lucide-react";
import type { ReactNode } from "react";
import useViewPortWidth from "src/utils/useViewPortWidth";

interface FreeTrialLockProps {
  children: ReactNode;
  isLocked?: boolean;
  variant?: "subtle" | "badge" | "overlay" | "disabled";
  lockIconSize?: number;
  lockSpacing?: number;
  disabled?: boolean;
  onClick?: () => void;
  isRedesignYourLifeBtn?: boolean;
  fullWidth?: boolean;
}

const FreeTrialLock: React.FC<FreeTrialLockProps> = ({
  children,
  isLocked = false,
  variant = "badge",
  lockIconSize = 12,
  lockSpacing = 2,
  disabled = false,
  isRedesignYourLifeBtn = false,
  onClick,
  fullWidth = false,
}) => {
  // Use color mode values for consistent theming
  const overlayBg = useColorModeValue("blackAlpha.100", "whiteAlpha.100");
  const badgeBg = useColorModeValue("orange.500", "orange.300");
  const { viewPortWidth } = useViewPortWidth();

  if (!isLocked) {
    return <>{children}</>;
  }

  const renderLockedContent = (): JSX.Element => {
    switch (variant) {
      case "badge":
        return (
          <Box
            position="relative"
            display={fullWidth ? "block" : "inline-block"}
            style={fullWidth ? { width: "100%" } : {}}
            onClick={onClick}
          >
            <Box
              filter="grayscale(60%)"
              opacity={0.6}
              transition="all 0.2s ease"
            >
              <div style={{ pointerEvents: "none" }}>{children}</div>
            </Box>
            {/* @ts-ignore */}
            <Badge
              position="absolute"
              top={`-${lockSpacing}px`}
              right={`${
                isRedesignYourLifeBtn
                  ? viewPortWidth >= 768
                    ? "40px"
                    : "-40px"
                  : `-${lockSpacing}px`
              }`}
              colorScheme="orange"
              borderRadius="full"
              fontSize="xs"
              px={1}
              py={0.5}
              display="flex"
              alignItems="center"
              gap={1}
              zIndex={10}
            >
              <Lock size={lockIconSize} />
            </Badge>
          </Box>
        );

      case "overlay":
        return (
          <Box position="relative" display="block" w="100%" onClick={onClick}>
            <Box
              filter="grayscale(40%)"
              opacity={0.7}
              pointerEvents="none"
              userSelect="none"
              transition="all 0.2s ease"
            >
              {children}
            </Box>
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg={overlayBg}
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
              pointerEvents="none"
              zIndex={10}
            >
              <Box
                bg={badgeBg}
                color="white"
                borderRadius="full"
                p={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Lock size={lockIconSize} />
              </Box>
            </Box>
          </Box>
        );

      case "disabled":
        return (
          <Box
            filter="grayscale(50%)"
            opacity={0.5}
            pointerEvents="none"
            userSelect="none"
            transition="all 0.2s ease"
            position="relative"
            w="100%"
          >
            {children}
          </Box>
        );

      case "subtle":
      default:
        return (
          <Box
            filter="grayscale(30%)"
            opacity={0.8}
            pointerEvents="none"
            userSelect="none"
            transition="all 0.2s ease"
            position="relative"
            w="100%"
            _hover={{
              opacity: 0.6,
            }}
          >
            {children}
          </Box>
        );
    }
  };

  const lockedContent = renderLockedContent();

  return lockedContent;
};

export default FreeTrialLock;
